import { acquireApiToken } from '../auth/authService.js';

// 자료실(파일 탐색기) API — path 는 자료실 루트 기준 상대경로("교육자료/2026", 루트 = "")
export interface LibraryItem {
  id: string; name: string; folder: boolean; size: number; modifiedAt: string; childCount?: number;
  owner?: string; ownerName?: string;
}
export interface TrashItem extends LibraryItem {
  originalName: string; originalPath: string; deletedBy: string; deletedByName: string; deletedAt: string;
}

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

export async function call(path: string, body?: unknown): Promise<Response> {
  const token = await acquireApiToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: { ...(body === undefined ? {} : { 'content-type': 'application/json' }), ...(token ? { authorization: `Bearer ${token}` } : {}) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const msg = await res.json().then((b: { message?: string }) => b?.message, () => undefined);
    throw new Error(msg ?? `API ${res.status} ${path}`);
  }
  return res;
}

export const libraryApi = {
  list: async (path: string) => (await (await call(`/library?path=${encodeURIComponent(path)}`)).json()) as { items: LibraryItem[] },
  createFolder: (path: string, name: string) => call('/library/folder', { path, name }),
  /** 업로드(최대 500MB) — 세션을 열고 8MB 조각을 순서대로 보냄. onProgress(0~1) */
  upload: async (path: string, file: File, onProgress?: (ratio: number) => void): Promise<void> => {
    const { sid, chunkSize } = (await (await call('/library/upload/start', { path, fileName: file.name, size: file.size })).json()) as { sid: string; chunkSize: number };
    for (let start = 0; start < file.size; start += chunkSize) {
      const data = await new Promise<string>((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result).split(',')[1] ?? '');
        fr.onerror = () => reject(new Error('파일 읽기 실패'));
        fr.readAsDataURL(file.slice(start, start + chunkSize));
      });
      await call('/library/upload/chunk', { sid, start, data });
      onProgress?.(Math.min(1, (start + chunkSize) / file.size));
    }
  },
  rename: (id: string, name: string) => call('/library/rename', { id, name }),
  remove: (id: string) => call('/library/delete', { id }), // 휴지통으로 이동
  trash: async () => (await (await call('/library/trash')).json()) as { items: TrashItem[] },
  restore: async (id: string) => (await (await call('/library/trash/restore', { id })).json()) as { path: string },
  purge: (id: string) => call('/library/trash/purge', { id }),
  download: async (id: string, fileName: string): Promise<void> => {
    const blob = await (await call(`/library/content?id=${encodeURIComponent(id)}`)).blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = fileName; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};
